import { QueueTablesArgs } from "@/shared/Executions/models/QueueTablesArgs";
import { Table } from "../models/Table";
import { TableField } from "../models/TableField";
import { TableFieldType } from "../models/TableFieldType";
import { TableSchema } from "../models/TableSchema";
import { TablesTypeGuardService } from "./TablesTypeGuardService";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";

export class TablesService {

    public static readonly CUSTOM_TABLE_TYPE = "unknown";

    public static getFlattenedTable(table: Table, includeType: string | null): Table {
        if (table == null) throw new Error("table is required");

        const tableObject = table.configuration ?? table;
        const { _type, ...properties } = tableObject || {};
        const flattenedTable = { ...properties } as Table;
        if (includeType != null) {
            flattenedTable.type = includeType;
        }
        return flattenedTable;
    }

    public static createTableFromTableSchema(tableSchema: TableSchema): Table {
        const newTable: Table = {
            id: this.generateTableId(),
            _type: tableSchema.type,
            display_name: tableSchema.display_name,
            description: 'New table configuration',
            category: tableSchema.category,
            configuration: {},
            schema: tableSchema
        };
        return newTable;
    }

    public static createTableFromTableField(tableField: TableField): Table {
        const newTable: Table = {
            id: this.generateTableId(),
            _type: tableField.field_type,
            display_name: tableField.display_name,
            display_field: tableField.display_field,
            description: tableField.description,
            category: "Default Value",
            configuration: {},
            schema: tableField
        };
        return newTable;
    }

    public static generateTableId(): string {
        const id = `table-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeTableConfiguration(table: Table, fields: Array<TableField>): Record<string, any> {
        const config: Record<string, any> = {};
        config.description = `${table.display_name ?? "Table"} Description`;
        fields.forEach((field: TableField) => {
            this.initializeTableConfigurationForField(field, config);
        });
        config._type = table._type;
        return config;
    }

    public static initializeTableFieldConfiguration(fields: Array<TableField>): Record<string, any> {
        const config: Record<string, any> = {};
        fields.forEach((field: TableField) => {
            this.initializeTableConfigurationForField(field, config);
        });
        return config;
    }

    public static initializeTableConfigurationForField(field: TableField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value;
        } else if (field.field_type === TableFieldType.Boolean) {
            config[field.name] = false;
        } else if (
            field.field_type === TableFieldType.MultiSelect ||
            field.field_type === TableFieldType.ColumnMultiSelect ||
            field.field_type === TableFieldType.Array ||
            field.field_type === TableFieldType.ColumnMultiSelectOrCreate
        ) {
            config[field.name] = [];
        } else if (
            field.field_type === TableFieldType.Object ||
            field.field_type === TableFieldType.StringTemplate
        ) {
            config[field.name] = {};
        } else {
            config[field.name] = '';
        }
        return config;
    }

    public static createQueueTablesArgs(
        parentExecutionId: string,
        flattenedTables: Array<Table>,
        name?: string): QueueTablesArgs {

        const args = new QueueTablesArgs(
            flattenedTables,
            parentExecutionId,
            name
        )

        return args
    }

    public static getFormattedTablesJson(parentExecutionId: string, executionName: string | undefined, tables: Array<Table>): IQueueExecutionRequest {
        const flattenedTables = this.getFlattenedTables(tables);
        const args = TablesService.createQueueTablesArgs(parentExecutionId, flattenedTables, executionName);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', tableArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattenedTables(tables: Array<Table>): Array<Table> {
        const flattenedTables = (tables || []).map((x) => TablesService.getFlattenedTable(x, x._type));
        this.recursivelyFlattenTables(flattenedTables);
        return flattenedTables;
    }

    private static recursivelyFlattenTables(tables: Array<Table | Object>, visited = new Set<any>()) {
        for (const table of tables) {
            if (visited.has(table)) continue;
            visited.add(table);

            for (const key of Object.keys(table)) {
                const value = (table as any)[key];

                if (Array.isArray(value)) {
                    const containsSubTables =
                        value.length > 0 &&
                        value.every((item) => item && TablesTypeGuardService.isTable(item));

                    if (containsSubTables) {
                        const flattenedSubTables = value.map((subTable: Table) => {
                            const includeType = !this.isInternalSchemaType(subTable._type) ? subTable._type : null;
                            return this.getFlattenedTable(subTable, includeType);
                        });

                        (table as any)[key] = flattenedSubTables;
                        this.recursivelyFlattenTables(flattenedSubTables, visited);
                    }
                }
                else if (TablesTypeGuardService.isNonEmptyTableObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct table object
                        if (TablesTypeGuardService.isTable(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedTable(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenTables([flattened], visited);
                        }

                        // Case 2: Another nested non-empty table-like object
                        else if (TablesTypeGuardService.isNonEmptyTableObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenTables([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const tables = this.extractTablesFromArray(nestedValue);
                            if (tables.length > 0) {
                                const flattened = tables.map(subTable => {
                                    const includeType = !this.isInternalSchemaType(subTable._type) ? subTable._type : null;
                                    return this.getFlattenedTable(subTable, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenTables(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractTablesFromArray(arr: unknown[]): Table[] {
        return arr.filter(TablesTypeGuardService.isTable);
    }

    public static getFormattedTablesJsonString(parentExecutionId: string, executionName: string | undefined, tables: Array<Table>, tablesOnly: boolean = false): string {
        const payload = TablesService.getFormattedTablesJson(parentExecutionId, executionName, tables);
        return JSON.stringify((tablesOnly ? payload.customTables : payload), null, 2);
    }

    public static tryGetTableSchemaByType(type: string, tableSchemas: Array<TableSchema>): TableSchema | undefined {
        if (type == null) throw new Error("type is required");
        if (tableSchemas == null) throw new Error("tableSchemas are required");
        return tableSchemas.find(x => x.type === type) || tableSchemas.find(x => x.type === TablesService.CUSTOM_TABLE_TYPE);
    }

    // If you have internal schema types for tables, implement this
    public static isInternalSchemaType(type: string): boolean {
        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;
    }
}
